import React, { useEffect } from 'react';
import { Button, Dropdown, Form, Grid, Modal } from 'semantic-ui-react';
import { SelectField, TextField } from '../../common/FormFields';
import { connect } from 'react-redux';
import * as actions from '../../../redux/actions';
import * as selectors from '../../../redux/selectors';
import { bindActionCreators } from 'redux';
import { Formik } from 'formik';
import { listingInitialValues, listingValidationSchema } from '../adslot.constants';
import CloudinaryUploadWidget from '../CloudinaryUploadWidget';
import * as moment from 'moment-timezone';
import { DateTimeInput } from 'semantic-ui-calendar-react';

const AddListingModal = ({ addListing, getFirms, firm, firmOptions, typeOptions, repOptions, getRepsForFirm }) => {
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (!firm?.id) return;
    getRepsForFirm(firm.id);
  }, [firm]);

  const handleSubmit = (listing, formik) => {
    addListing(listing);
    setOpen(false);
    getFirms();
    formik.resetForm();
  };

  return (
    <Formik
      initialValues={{ ...listingInitialValues, showcaseFirmId: firm.id }}
      enableReinitialize
      validationSchema={listingValidationSchema}
      onSubmit={handleSubmit}
    >
      {formik => (
        <Modal
          size="large"
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          open={open}
          trigger={<Button content="New listing" icon="plus" positive labelPosition="left" />}
        >
          <Modal.Header>Create listing</Modal.Header>
          <Modal.Content scrolling>
            <Form onSubmit={formik.handleSubmit} widths="equal" size="tiny">
              <Form.Group>
                <Form.Field>
                  <label>Firm</label>
                  <Dropdown
                    fluid
                    search
                    clearable
                    selection
                    options={firmOptions}
                    id="showcaseFirmId"
                    value={formik.values.showcaseFirmId}
                    onChange={(e, d) => {
                      formik.setFieldValue('showcaseFirmId', d.value);
                      getRepsForFirm(d.value);
                    }}
                  />
                </Form.Field>

                <SelectField
                  name="type"
                  options={typeOptions}
                  onChange={formik.handleChange}
                  value={formik.values.type}
                  label="Type"
                  formik={formik}
                />
              </Form.Group>

              {formik.values.type !== 'Product' && (
                <>
                  <Form.Group>
                    <SelectField
                      name="representativeId"
                      options={repOptions}
                      onChange={formik.handleChange}
                      value={formik.values.representativeId}
                      label="Realtor"
                      formik={formik}
                    />
                    <Form.Field>
                      <label>Open House Start Date & Time</label>
                      <DateTimeInput
                        name="openHouseStartDate"
                        value={
                          formik?.values?.openHouseStartDate
                            ? moment(formik.values.openHouseStartDate).format('LLLL')
                            : ''
                        }
                        iconPosition="left"
                        dateFormat="LLLL"
                        startMode="day"
                        timeFormat="AMPM"
                        onChange={(e, { value }) => formik.setFieldValue('openHouseStartDate', moment(value, 'LLLL'))}
                      />
                    </Form.Field>

                    <Form.Field>
                      <label>Open House End Date & Time</label>
                      <DateTimeInput
                        name="openHouseEndDate"
                        startMode="day"
                        value={
                          formik?.values?.openHouseEndDate ? moment(formik.values.openHouseEndDate).format('LLLL') : ''
                        }
                        iconPosition="left"
                        dateFormat="LLLL"
                        timeFormat="AMPM"
                        onChange={(e, { value }) => formik.setFieldValue('openHouseEndDate', moment(value, 'LLLL'))}
                      />
                    </Form.Field>
                  </Form.Group>
                </>
              )}

              <Form.Group>
                <TextField
                  name="description1"
                  formik={formik}
                  value={formik.values.description1}
                  onChange={formik.handleChange}
                  error={formik.touched.description1 && Boolean(formik.errors.description1)}
                  errorMessage={formik.errors.description1}
                  label="Description 1"
                />

                <TextField
                  name="description2"
                  formik={formik}
                  value={formik.values.description2}
                  onChange={formik.handleChange}
                  error={formik.touched.description2 && Boolean(formik.errors.description2)}
                  errorMessage={formik.errors.description2}
                  label="Description 2"
                />
              </Form.Group>

              <Form.Group>
                <TextField
                  name="url"
                  formik={formik}
                  value={formik.values.url}
                  onChange={formik.handleChange}
                  error={formik.touched.url && Boolean(formik.errors.url)}
                  errorMessage={formik.errors.url}
                  label="URL"
                />

                <Form.Field>
                  <Grid columns="2" style={{ marginBottom: 0 }}>
                    <Grid.Column width="12">
                      <TextField
                        name="imageUrl"
                        formik={formik}
                        value={formik.values.imageUrl}
                        onChange={formik.handleChange}
                        error={formik.touched.imageUrl && Boolean(formik.errors.imageUrl)}
                        errorMessage={formik.errors.imageUrl}
                        label="Image URL"
                      />
                    </Grid.Column>
                    <Grid.Column style={{ paddingTop: 37 }} width="4">
                      <CloudinaryUploadWidget formik={formik} fieldName={'imageUrl'} />
                    </Grid.Column>
                  </Grid>
                </Form.Field>
              </Form.Group>

              {formik.values.type === 'Product' && (
                <>
                  <TextField
                    name="line1"
                    formik={formik}
                    value={formik.values.line1}
                    onChange={formik.handleChange}
                    error={formik.touched.line1 && Boolean(formik.errors.line1)}
                    errorMessage={formik.errors.line1}
                    label="Line 1"
                  />

                  <TextField
                    name="line2"
                    formik={formik}
                    value={formik.values.line2}
                    onChange={formik.handleChange}
                    error={formik.touched.line2 && Boolean(formik.errors.line2)}
                    errorMessage={formik.errors.line2}
                    label="Line 2"
                  />

                  <TextField
                    name="line3"
                    formik={formik}
                    value={formik.values.line3}
                    onChange={formik.handleChange}
                    error={formik.touched.line3 && Boolean(formik.errors.line3)}
                    errorMessage={formik.errors.line3}
                    label="Line 3"
                  />
                </>
              )}

              <TextField
                name="price"
                formik={formik}
                value={formik.values.price}
                onChange={formik.handleChange}
                error={formik.touched.price && Boolean(formik.errors.price)}
                errorMessage={formik.errors.price}
                label="Price"
              />

              {formik.values.type !== 'Product' && (
                <>
                  <Form.Group>
                    <TextField
                      name="address"
                      formik={formik}
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      error={formik.touched.address && Boolean(formik.errors.address)}
                      errorMessage={formik.errors.address}
                      label="Address"
                    />
                    <TextField
                      name="city"
                      formik={formik}
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      error={formik.touched.city && Boolean(formik.errors.city)}
                      errorMessage={formik.errors.city}
                      label="City"
                    />
                  </Form.Group>

                  <Form.Group>
                    <TextField
                      name="state"
                      formik={formik}
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      error={formik.touched.state && Boolean(formik.errors.state)}
                      errorMessage={formik.errors.state}
                      label="State"
                    />
                    <TextField
                      name="zip"
                      formik={formik}
                      value={formik.values.zip}
                      onChange={formik.handleChange}
                      error={formik.touched.zip && Boolean(formik.errors.zip)}
                      errorMessage={formik.errors.zip}
                      label="Zip"
                    />
                  </Form.Group>
                </>
              )}
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button color="black" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button content="Save" labelPosition="left" icon="save" onClick={formik.handleSubmit} positive />
          </Modal.Actions>
        </Modal>
      )}
    </Formik>
  );
};

const mapStateToProps = state => ({
  firm: selectors.getCurrentFirm(state),
  firmOptions: selectors.firms.firmLookupOptions(state),
  repOptions: selectors.reps.repOptions(state),
  typeOptions: selectors.getTypeOptions(state),
});

const mapDispatchToProps = dispatch => ({
  addListing: bindActionCreators(actions.listing.addListing, dispatch),
  getRepsForFirm: bindActionCreators(actions.rep.getRepsForFirm, dispatch),
  getFirms: bindActionCreators(actions.firm.getFirms, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddListingModal);
